import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getData, postData } from '../../../../services/apiService';

const API_ENDPOINTS = {
  GET_ALL_ATTRIBUTES: 'GetAllAttrval',
  CREATE_ATTRIBUTE: 'Event/CreateAttribute'
};

// Custom hook for API calls
const useAttributes = () => {
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAttributes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getData(API_ENDPOINTS.GET_ALL_ATTRIBUTES);

      if (!response) {
        throw new Error('No response received from server');
      }

      if (!response.Result) {
        throw new Error('Invalid response format: Result field missing');
      }

      if (!Array.isArray(response.Result)) {
        throw new Error('Invalid response format: Result is not an array');
      }

      setAttributes(response.Result);
      return response.Result;

    } catch (error) {
      console.error('Error fetching attributes:', error);
      const apiError = {
        message: error.message || 'Failed to fetch attributes',
        type: 'API_ERROR'
      };
      setError(apiError);
      throw apiError;
    } finally {
      setLoading(false);
    }
  }, []);

  const createAttribute = useCallback(async (attrData) => {
    try {
      setLoading(true);
      setError(null);

      const requestBody = {
        Attr_Id: attrData.Attr_Id || 0,
        Attr_Name: attrData.Attr_Name || '',
        Attr_Status: attrData.Attr_Status || 'Active',
        Message: attrData.Message || ''
      };

      const response = await postData(API_ENDPOINTS.CREATE_ATTRIBUTE, requestBody);

      if (!response) {
        throw new Error('No response received from server');
      }

      return response;

    } catch (error) {
      console.error('Error creating attribute:', error);
      const apiError = {
        message: error.message || 'Failed to create attribute',
        type: 'API_ERROR'
      };
      setError(apiError);
      throw apiError;
    } finally {
      setLoading(false);
    }
  }, []);

  return { 
    attributes, 
    loading, 
    error, 
    fetchAttributes, 
    createAttribute
  };
};

// Utility functions
const AttributeUtils = {
  checkDuplicateChild: (attributes, parentId, name) => {
    const parent = attributes.find(p => p.Id === parentId);
    if (!parent || !parent.Children) return false;

    return parent.Children.some(child => 
      child.Name.toLowerCase() === name.toLowerCase()
    );
  },

  validateAttribute: (attribute) => {
    if (!attribute.name?.trim()) {
      return 'Please enter a value name';
    }

    return null;
  }
};

// Sub-components for better organization
const SearchHeader = ({ searchTerm, onSearchChange, title, description }) => (
  <div className="flex-shrink-0 p-6 border-b border-blue-100 bg-blue-600 text-white">
    <div className="flex justify-between items-center mb-4">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-blue-500 rounded-xl">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
          </svg>
        </div>
        <div>
          <h2 className="text-xl font-bold">{title}</h2>
          <p className="text-blue-100 text-sm opacity-90">{description}</p>
        </div>
      </div>
    </div>

    <div className="relative">
      <input
        type="text"
        placeholder="Search categories..."
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        className="w-full px-4 py-3 border outline-none text-black border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-300 focus:border-blue-300 transition-all bg-white"
      />
    </div>
  </div>
);

const AddButton = ({ onClick, label, icon, fullWidth = false }) => (
  <button
    onClick={onClick}
    className={`${fullWidth ? 'w-full' : ''} bg-blue-600 text-white px-4 py-3 rounded-xl hover:bg-blue-700 transition-all duration-300 transform hover:scale-[1.02] shadow-md hover:shadow-lg flex items-center justify-center gap-3 font-semibold group`}
  >
    {icon}
    {label}
  </button>
);

const ParentListItem = ({ parent, isSelected, onClick }) => (
  <div
    onClick={onClick}
    className={`p-4 border-b border-blue-100 cursor-pointer transition-all duration-300 group relative overflow-hidden ${
      isSelected
        ? 'bg-blue-50 border-l-4 border-l-blue-600 shadow-sm'
        : 'hover:bg-blue-50 border-l-4 border-l-transparent'
    }`}
  >
    <div className="flex justify-between items-start relative z-10">
      <div className="flex-1 min-w-0">
        <h3 className={`font-semibold text-lg truncate group-hover:text-blue-700 transition-colors ${
          isSelected ? 'text-blue-700' : 'text-gray-800'
        }`}>
          {parent.Header_Name}
        </h3>
        <div className="flex items-center gap-2 mt-2">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {parent.Children?.length || 0} values
          </span>
        </div>
      </div>
      {isSelected && (
        <div className="flex items-center justify-center w-6 h-6 bg-blue-600 rounded-full shadow-sm">
          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      )}
    </div>
  </div>
);

const ChildCard = ({ child }) => (
  <div className="bg-white border border-blue-200 rounded-xl p-5 hover:shadow-lg transition-all duration-300 group overflow-hidden">
    <div className="flex justify-between items-start mb-3 relative z-10">
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-gray-900 text-lg group-hover:text-blue-600 transition-colors line-clamp-2 mb-2">
          {child.Name}
        </h3>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            ID: {child.ValueCode}
          </span>
        </div>
      </div>
    </div>
  </div>
);

const AttributeModal = ({ 
  isOpen, 
  onClose, 
  attribute, 
  onAttributeChange, 
  onSave, 
  error, 
  loading = false,
  parentName 
}) => {
  if (!isOpen) return null;

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && attribute.name.trim()) {
      onSave();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl transform transition-all border border-blue-200">
        <div className="mb-6">
          {/* Modal Header with Parent Name */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <h3 className="text-xl font-bold text-blue-700">{parentName}</h3>
              </div>
              <p className="text-sm text-gray-600 ml-6">
                <span className="font-semibold text-gray-700 ml-1">Add New Value</span>
              </p>
            </div>
            <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M7 2a1 1 0 00-.707 1.707L7 4.414v3.758a1 1 0 01-.293.707l-4 4C.817 14.769 2.156 18 4.828 18h10.343c2.673 0 4.012-3.231 2.122-5.121l-4-4A1 1 0 0113 8.172V4.414l.707-.707A1 1 0 0013 2H7zm2 6.172V4h2v4.172a3 3 0 00.879 2.12l1.027 1.028a4 4 0 00-2.171.102l-.47.156a4 4 0 01-2.53 0l-.563-.187a1.993 1.993 0 00-.114-.035l1.063-1.063A3 3 0 009 8.172z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
            <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <p className="text-red-700 text-sm font-medium">{error}</p>
          </div>
        )}

        <div className="space-y-5" onKeyPress={handleKeyPress}>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Attribute Name *
            </label>
            <input
              type="text"
              value={attribute.name}
              onChange={(e) => onAttributeChange('name', e.target.value)}
              className="w-full px-4 py-3 border outline-none border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-300 focus:border-blue-300 transition-all bg-white"
              placeholder="Enter value name"
              autoFocus
            />
          </div>
        </div>

        <div className="flex gap-3 mt-8">
          <button
            onClick={onSave}
            disabled={loading}
            className={`flex-1 py-3 rounded-xl text-white font-semibold transition-all duration-300 transform hover:scale-[1.02] shadow-lg bg-blue-600 hover:bg-blue-700 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Adding...
              </span>
            ) : 'Add Value'}
          </button>
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-xl hover:bg-gray-300 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const AttributeMaster = () => {
  const { 
    attributes, 
    loading, 
    error: apiError, 
    fetchAttributes, 
    createAttribute
  } = useAttributes();
  
  const [selectedParent, setSelectedParent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newAttribute, setNewAttribute] = useState({ 
    name: ''
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0); // Add refresh key to force update

  // Initialize data
  useEffect(() => {
    const initializeData = async () => {
      try {
        const fetchedAttributes = await fetchAttributes();
        if (fetchedAttributes.length > 0) {
          setSelectedParent(fetchedAttributes[0]);
        }
      } catch (error) {
        console.error('Initialization error:', error);
      }
    };

    initializeData();
  }, [fetchAttributes]);

  // Update selectedParent when attributes change
  useEffect(() => {
    if (selectedParent && attributes.length > 0) {
      const updatedParent = attributes.find(p => p.Id === selectedParent.Id);
      if (updatedParent) {
        setSelectedParent(updatedParent);
      }
    }
  }, [attributes]); // Add this effect to update selectedParent

  // Memoized filtered parents for performance
  const filteredParents = useMemo(() => 
    attributes.filter(parent =>
      parent.Header_Name.toLowerCase().includes(searchTerm.toLowerCase())
    ),
    [attributes, searchTerm]
  );

  // Handler functions
  const handleAddAttribute = async () => {
    setError('');

    const validationError = AttributeUtils.validateAttribute(newAttribute);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!selectedParent) {
      setError('Please select a category first');
      return;
    }

    if (AttributeUtils.checkDuplicateChild(
      attributes, 
      selectedParent.Id, 
      newAttribute.name
    )) {
      setError('A value with this name already exists in this category');
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Prepare API request body
      const attributeData = {
        Attr_Id: selectedParent.Id,
        Attr_Name: newAttribute.name.trim(),
        Attr_Status: "Active",
        Message: ""
      };

      // Call the API to create attribute
      await createAttribute(attributeData);
      
      // Refresh the list from server after successful creation
      await fetchAttributes();
      
      // Force UI update
      setRefreshKey(prev => prev + 1);
      
      handleCloseModal();
      
    } catch (error) {
      console.error('Error creating attribute:', error);
      setError(error.message || 'Failed to create attribute. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAttributeChange = (field, value) => {
    setNewAttribute(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleCloseModal = () => {
    setIsAddModalOpen(false);
    setNewAttribute({ name: '' });
    setError('');
    setIsSubmitting(false);
  };

  // Loading state
  if (loading && !isSubmitting) {
    return (
      <div className="h-[calc(100vh-190px)] overflow-hidden bg-blue-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading attributes...</p>
        </div>
      </div>
    );
  }

  // API Error state
  if (apiError) {
    return (
      <div className="h-[calc(100vh-190px)] overflow-hidden bg-blue-50 p-6 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Failed to Load Data</h3>
          <p className="text-gray-600 mb-4">{apiError.message}</p>
          <button
            onClick={fetchAttributes}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-190px)] overflow-hidden bg-blue-50 p-6">
      <div className="w-full mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" style={{ minHeight: '80vh', maxHeight: '90vh' }}>

          {/* Left Panel - Parent Attributes */}
          <div className="lg:col-span-1 flex flex-col h-full">
            <div className="bg-white rounded-2xl shadow-lg border border-blue-200 overflow-hidden flex flex-col h-full">
              <SearchHeader
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                title="Categories"
                description="Select a category to manage values"
              />

              <div className="flex-1 overflow-hidden">
                <div className="h-[calc(100vh-375px)] overflow-y-auto custom-scrollbar p-2">
                  {filteredParents.map((parent) => (
                    <ParentListItem
                      key={`${parent.Id}-${refreshKey}`} // Add refreshKey to force re-render
                      parent={parent}
                      isSelected={selectedParent?.Id === parent.Id}
                      onClick={() => setSelectedParent(parent)}
                    />
                  ))}

                  {filteredParents.length === 0 && (
                    <div className="p-8 text-center h-full flex items-center justify-center">
                      <div>
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <p className="text-gray-500 font-medium">No categories found</p>
                        <p className="text-gray-400 text-sm mt-1">Try adjusting your search</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Children Attributes */}
          <div className="lg:col-span-2 flex flex-col h-full">
            <div className="bg-white rounded-2xl shadow-lg border border-blue-200 overflow-hidden flex flex-col h-full">
              {selectedParent ? (
                <div className="flex flex-col h-full" key={`${selectedParent.Id}-${refreshKey}`}>
                  <div className="flex-shrink-0 p-6 border-b border-blue-100 bg-blue-50">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                          <h2 className="text-2xl font-bold text-gray-900 truncate">
                            {selectedParent.Header_Name}
                          </h2>
                        </div>
                        <p className="text-gray-600 flex items-center gap-2">
                          <span>Manage attribute values for this category</span>
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            {selectedParent.Children?.length || 0} items
                          </span>
                        </p>
                      </div>
                      <AddButton
                        onClick={() => {
                          setNewAttribute({ name: '' });
                          setIsAddModalOpen(true);
                        }}
                        label="Add Value"
                        icon={
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        }
                      />
                    </div>
                  </div>

                  <div className="flex-1 overflow-hidden">
                    <div className="h-full overflow-y-auto custom-scrollbar">
                      <div className="h-[calc(100vh-325px)] overflow-y-auto custom-scrollbar p-2">
                        {selectedParent.Children?.length > 0 ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {selectedParent.Children.map((child, index) => (
                              <ChildCard
                                key={`${child.Id}-${index}-${refreshKey}`} // Add refreshKey to force re-render
                                child={child}
                              />
                            ))}
                          </div>
                        ) : (
                          <div className="h-full flex items-center justify-center min-h-[400px]">
                            <div className="text-center max-w-md mx-auto">
                              <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                <svg className="w-12 h-12 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </div>
                              <h3 className="text-xl font-semibold text-gray-900 mb-3">No values yet</h3>
                              <p className="text-gray-500 mb-6">
                                Start by adding values to organize your <span className="font-semibold text-blue-600">{selectedParent.Header_Name.toLowerCase()}</span> category.
                              </p>
                              <AddButton
                                onClick={() => {
                                  setNewAttribute({ name: '' });
                                  setIsAddModalOpen(true);
                                }}
                                label="Create First Value"
                                icon={
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                  </svg>
                                }
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center max-w-sm mx-auto">
                    <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <svg className="w-10 h-10 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">Select a Category</h3>
                    <p className="text-gray-500">
                      Choose a category from the left panel to view and manage its attribute values.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Modal */}
      <AttributeModal
        isOpen={isAddModalOpen}
        onClose={handleCloseModal}
        attribute={newAttribute}
        onAttributeChange={handleAttributeChange}
        onSave={handleAddAttribute}
        error={error}
        loading={isSubmitting}
        parentName={selectedParent?.Header_Name} // Pass parent name to modal
      />
    </div>
  );
};

export default AttributeMaster;